import { Calendar } from "@/components/ui/calendar";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn, selectAllOnFocus } from "@/lib/utils";
import { format, isValid, parse, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import { useEffect, useState } from "react";
import type { FieldValues, Path, UseFormReturn } from "react-hook-form";
import { IMaskInput } from "react-imask";
import { Button } from "./button";

interface DateFieldProps<TFieldValues extends FieldValues> {
  form: UseFormReturn<TFieldValues>;
  name: Path<TFieldValues>;
  label: string;
  placeholder?: string;
  className?: string;
  maxDate?: Date;
}

const DateField = <TFieldValues extends FieldValues>({
  form,
  name,
  label,
  placeholder = "Selecione uma data",
  className,
  maxDate,
}: DateFieldProps<TFieldValues>) => {
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [inputValue, setInputValue] = useState<string>("");

  const handleDateChange = (
    field: FieldValues,
    date: Date | undefined,
    setOpen: (open: boolean) => void,
  ) => {
    if (date && isValid(date)) {
      const dateString = format(date, "yyyy-MM-dd");
      field.onChange(dateString);
      setOpen(false);
    }
  };

  const handleInputChange = (field: FieldValues, value: string) => {
    setInputValue(value);

    const digitsOnly = value.replace(/\D/g, "");

    if (digitsOnly.length === 0) {
      field.onChange("");
      return;
    }

    if (digitsOnly.length === 8) {
      const parsedDate = parse(value, "dd/MM/yyyy", new Date());
      if (isValid(parsedDate)) {
        const dateString = format(parsedDate, "yyyy-MM-dd");
        field.onChange(dateString);
      }
    }
  };

  const watchedValue = form.watch(name);

  useEffect(() => {
    if (watchedValue && typeof watchedValue === "string") {
      try {
        const parsed = parseISO(watchedValue);
        if (isValid(parsed)) {
          setInputValue(format(parsed, "dd/MM/yyyy"));
          return;
        }
      } catch {
        // ignore parse errors, keep current inputValue
      }
    }

    if (!watchedValue) {
      setInputValue("");
    }
  }, [watchedValue]);

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem className={cn("flex flex-col", className)}>
          <FormLabel>{label}</FormLabel>
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <div className="flex border rounded-md">
              <FormControl>
                <div className="relative grow">
                  <PopoverTrigger asChild>
                    <div className="w-full cursor-pointer">
                      <IMaskInput
                        mask="00/00/0000"
                        value={inputValue}
                        onAccept={(value: string) =>
                          handleInputChange(field, value)
                        }
                        onBlur={field.onBlur}
                        // A full mask rejects extra digits, so typing over a
                        // filled date only works from a full selection.
                        {...selectAllOnFocus}
                        placeholder={placeholder}
                        className={cn(
                          "bg-transparent outline-hidden text-sm flex w-full rounded-md px-3 py-2",
                          !field.value && "text-muted-foreground",
                        )}
                      />
                    </div>
                  </PopoverTrigger>
                </div>
              </FormControl>
              <div className="relative">
                <Button
                  type="button"
                  variant="ghost"
                  className="rounded-l-none"
                  onClick={() => setCalendarOpen(true)}
                >
                  <CalendarIcon className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <PopoverContent
              className="w-auto p-0"
              align="start"
              // Clicking the input opens the calendar; keep focus in the input
              // so typed digits land there instead of on a calendar day.
              onOpenAutoFocus={(e) => e.preventDefault()}
            >
              <Calendar
                locale={ptBR}
                mode="single"
                selected={
                  field.value && isValid(parseISO(field.value))
                    ? parseISO(field.value)
                    : undefined
                }
                onSelect={(date) =>
                  handleDateChange(field, date, setCalendarOpen)
                }
                defaultMonth={
                  field.value && isValid(parseISO(field.value))
                    ? parseISO(field.value)
                    : new Date()
                }
                disabled={maxDate ? { after: maxDate } : undefined}
                startMonth={new Date(2000, 0)}
                endMonth={new Date(2035, 11)}
              />
            </PopoverContent>
          </Popover>

          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export default DateField;
